import { SxProps, Theme } from "@mui/material";
import { SystemStyleObject } from "@mui/system";
import { button } from "theme/colors";
import contactsTokens, {
  contactsResponsiveTokens,
  PHONE_SM_QUERY,
} from "./Contacts.tokens";

const {
  page,
  header,
  secondaryButton,
  primaryButton,
  card,
  list,
  detail,
  field,
  formActions,
  freshState,
  skeleton,
} = contactsTokens;

const { phone, tablet } = contactsResponsiveTokens;

/**
 * Responsive geometry every control on the form shares. The desktop values are
 * untouched at `md`; `sm` is the tablet reference's 40px/9px box and `xs` the
 * phone's 44px (375) / 46px (390) one.
 */
const fieldHeight = {
  xs: phone.field.height.std,
  sm: tablet.field.height,
  md: field.height,
};
const fieldHeightNarrow = { height: phone.field.height.sm };

const fieldRadius = {
  xs: field.radius,
  sm: tablet.field.radius,
  md: field.radius,
};

/**
 * Type metrics of a field label. Shared with its skeleton: a text `Skeleton`
 * sizes its box from `fontSize` x line-height, so borrowing the real rules is
 * what keeps the placeholder in the same line box as the label.
 */
const contactsStylesFieldLabel = {
  fontSize: {
    xs: phone.field.labelFontSize.std,
    sm: tablet.field.labelFontSize,
    md: field.labelFontSize,
  },
  fontWeight: field.labelFontWeight,
  lineHeight: "normal",
} as const;

const fieldFontSize = {
  xs: field.fontSize,
  sm: tablet.field.fontSize,
  md: field.fontSize,
};

/**
 * MUI draws the outlined border on a `fieldset` overlaid on the root, so the
 * root itself has no border and an inner offset is measured from the *outer*
 * edge. The design's inputs carry a real 1px border, so every horizontal inset
 * on an outlined control has to be the design value plus that border to land in
 * the same place.
 */
const withBorder = (inset: string) => `calc(${inset} + ${field.borderWidth})`;

/**
 * Inline "n/max" counters only exist on the desktop reference; the phone and
 * tablet columns draw plain fields, so the trailing 8px reserve for the counter
 * is only made below the right edge from `md` up.
 */
const counterInputPadding = {
  xs: `0 ${withBorder(field.paddingX)}`,
  sm: `0 ${withBorder(tablet.field.paddingX)}`,
  md: `0 8px 0 ${withBorder(field.paddingX)}`,
};

const plainInputPadding = {
  xs: `0 ${withBorder(field.paddingX)}`,
  sm: `0 ${withBorder(tablet.field.paddingX)}`,
  md: `0 ${withBorder(field.paddingX)}`,
};

/** The number half of the phone control runs 2px tighter on the tablet. */
const phoneNumberInputPadding = {
  xs: `0 ${withBorder(field.paddingX)}`,
  sm: `0 ${withBorder(tablet.field.phonePaddingX)}`,
  md: `0 8px 0 ${withBorder(field.paddingX)}`,
};

/** Shared chrome for every field box on the form (input, dial code, select). */
const fieldBox = {
  height: fieldHeight,
  bgcolor: field.bg,
  border: `1px solid ${field.border}`,
  borderRadius: fieldRadius,
  boxSizing: "border-box",
  [PHONE_SM_QUERY]: fieldHeightNarrow,
} as const;

/**
 * Outlined-input chrome shared by the text fields. `paddingRight` differs
 * between the counter and plain variants, so the input rule is layered on top.
 */
const outlinedField = {
  "& .MuiOutlinedInput-root": {
    height: fieldHeight,
    p: 0,
    borderRadius: fieldRadius,
    bgcolor: field.bg,
    [PHONE_SM_QUERY]: fieldHeightNarrow,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: field.border,
      borderWidth: "1px",
    },
    "&:hover": {
      bgcolor: field.bg,
      "& .MuiOutlinedInput-notchedOutline": { borderColor: field.border },
    },
    "&.Mui-focused": {
      bgcolor: field.bg,
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: field.focusBorder,
        borderWidth: "1px",
      },
    },
    "&.Mui-disabled": {
      bgcolor: field.disabledBg,
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: field.disabledBorder,
      },
    },
    // Keep the validation outline visible — the base rules above are more
    // specific than MUI's own error styling.
    "&.Mui-error .MuiOutlinedInput-notchedOutline": {
      borderColor: "error.main",
    },
  },
  "& .MuiOutlinedInput-input": {
    height: "100%",
    boxSizing: "border-box",
    fontSize: fieldFontSize,
    fontWeight: field.fontWeight,
    color: field.color,
    "&::placeholder": {
      color: field.placeholder,
      opacity: 1,
      fontSize: fieldFontSize,
      fontWeight: field.fontWeight,
    },
    "&.Mui-disabled": {
      WebkitTextFillColor: field.disabledColor,
      color: field.disabledColor,
    },
  },
  // Below `md` the reference has no counter, so the adornment is dropped
  // outright rather than shrunk — a 44px phone field has no room for it.
  "& .MuiInputAdornment-positionEnd": {
    display: { xs: "none", md: "flex" },
    mr: field.counterInset,
  },
  "& .MuiFormHelperText-root": {
    ml: 0,
    mt: "4px",
    fontSize: field.errorFontSize,
  },
} as const;

/** Row / column rhythm of the field grids, per column of the reference. */
const fieldRowGap = {
  xs: phone.field.rowGap.std,
  sm: tablet.field.rowGap,
  md: field.rowGap,
};

const fieldColumnGap = {
  xs: phone.field.columnGap.std,
  sm: tablet.field.columnGap,
  md: field.columnGap,
};

/**
 * Field-grid primitives kept as plain objects so the composed variants below
 * can spread them. Spreading an entry that has already been cast to
 * `SxProps<Theme>` widens it into a union `sx` will not accept back, so compose
 * here and cast once at the end.
 *
 * The narrow phone column stacks every pair, so its override has to restate the
 * template *and* the tighter gaps.
 */
const fieldGrid = {
  display: "grid",
  gridTemplateColumns: {
    xs: "minmax(0, 1fr) minmax(0, 1fr)",
    sm: "minmax(0, 1fr) minmax(0, 1fr)",
  },
  rowGap: fieldRowGap,
  columnGap: fieldColumnGap,
  [PHONE_SM_QUERY]: {
    gridTemplateColumns: "minmax(0, 1fr)",
    rowGap: phone.field.rowGap.sm,
    columnGap: phone.field.columnGap.sm,
  },
} as const;

/**
 * City / State / Zip / Country. Three columns from `sm` up with Country pulled
 * onto its own half-width row; two columns on the phone, where the reference
 * pairs City|State and Zip|Country instead.
 */
const fieldGridThirds = {
  display: "grid",
  gridTemplateColumns: {
    xs: "repeat(2, minmax(0, 1fr))",
    sm: "repeat(3, minmax(0, 1fr))",
  },
  rowGap: fieldRowGap,
  columnGap: fieldColumnGap,
  [PHONE_SM_QUERY]: {
    rowGap: phone.field.rowGap.sm,
    columnGap: phone.field.columnGap.sm,
  },
} as const;

/** Spacing between consecutive field blocks. */
const fieldBlock = {
  mt: fieldRowGap,
  [PHONE_SM_QUERY]: { marginTop: phone.field.rowGap.sm },
} as const;

/**
 * `{...a, ...b}` would drop `a`'s phone-column block on the floor — the raw
 * media query is a plain key, so the second spread simply replaces it. Merge
 * the two overrides explicitly instead.
 */
const mergePhoneQuery = (
  first: { [PHONE_SM_QUERY]?: object },
  second: { [PHONE_SM_QUERY]?: object },
) => ({
  ...first[PHONE_SM_QUERY],
  ...second[PHONE_SM_QUERY],
});

/** Loading-placeholder fill, shared by every skeleton on the screen. */
const skeletonFill = { bgcolor: skeleton.color } as const;

const contactsStyles = {
  /**
   * Inner gutters only — `CenteredAuthenticatedLayout` owns the centred measure.
   * The card then fills what is left of the viewport so the two panes scroll
   * inside it, exactly as the design does with `flex:1`.
   *
   * This has to be `height`, not `minHeight`. `minHeight` gives the column no
   * *definite* height, so the card's `flex:1` resolves against its own content
   * and the column stretches to fit the whole list instead of capping it —
   * which also means the list's `scrollHeight` never passes its `clientHeight`
   * and the fill-the-viewport effect keeps paging forever.
   *
   * Below `md` the phone and tablet references run the surface edge to edge and
   * viewport-tall, so the layout's own gutter, top margin and bottom padding
   * are pulled back off with negative margins and the screen keeps no gutters
   * of its own — each pane carries the reference's padding instead.
   */
  page: {
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    height: {
      xs: `calc(100vh - ${page.topNavHeight + page.topNavBorder}px)`,
      md: `calc(100vh - ${
        page.topNavHeight + page.layoutMarginTop.md + page.layoutPaddingBottom
      }px)`,
      lg: `calc(100vh - ${
        page.topNavHeight + page.layoutMarginTop.lg + page.layoutPaddingBottom
      }px)`,
    },
    mt: { xs: "-16px", sm: "-32px", md: 0 },
    mx: { xs: "-16px", sm: "-24px", md: 0 },
    mb: { xs: "-48px", md: 0 },
    pt: { xs: 0, md: page.paddingTop },
    px: { xs: 0, md: page.paddingX },
    pb: { xs: 0, md: page.paddingBottom },
  } as SxProps<Theme>,

  header: {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    alignItems: { xs: "flex-start", sm: "flex-end" },
    justifyContent: "space-between",
    gap: header.gap,
    mb: header.marginBottom,
  } as SxProps<Theme>,

  /**
   * Phone stand-in for the header block: the reference carries the screen name
   * in the app bar and keeps only the "693 total contacts" line on the page.
   */
  phoneHeader: {
    flexShrink: 0,
    pt: phone.paddingTop.std,
    px: phone.paddingX.std,
    pb: phone.gap.std,
    [PHONE_SM_QUERY]: {
      paddingTop: phone.paddingTop.sm,
      paddingLeft: phone.paddingX.sm,
      paddingRight: phone.paddingX.sm,
      paddingBottom: phone.gap.sm,
    },
  } as SxProps<Theme>,

  headerTitle: {
    m: 0,
    color: header.titleColor,
    fontSize: header.titleFontSize,
    fontWeight: header.titleFontWeight,
    letterSpacing: header.titleLetterSpacing,
    lineHeight: "normal",
  } as SxProps<Theme>,

  headerSubtitle: {
    mt: header.subtitleMarginTop,
    color: header.subtitleColor,
    fontSize: header.subtitleFontSize,
    lineHeight: "normal",
  } as SxProps<Theme>,

  /** Same line on the phone, where nothing sits above it. */
  phoneSubtitle: {
    m: 0,
    color: header.subtitleColor,
    fontSize: phone.subtitleFontSize.std,
    lineHeight: "normal",
    [PHONE_SM_QUERY]: { fontSize: phone.subtitleFontSize.sm },
  } as SxProps<Theme>,

  headerCount: {
    color: header.countColor,
    fontWeight: header.countFontWeight,
  } as SxProps<Theme>,

  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: header.gap,
    flexShrink: 0,
  } as SxProps<Theme>,

  // ------------------------------------------------------------- app bar slot

  /** "Contacts · 693 total" the tablet reference puts beside the hamburger. */
  appBarTitle: {
    display: "inline-flex",
    alignItems: "baseline",
    gap: tablet.appBarTitleGap,
    minWidth: 0,
  } as SxProps<Theme>,

  appBarCount: {
    fontSize: tablet.appBarCountFontSize,
    fontWeight: 500,
    color: tablet.appBarCountColor,
    flexShrink: 0,
  } as SxProps<Theme>,

  /** Outlined "Add" — the tablet's stand-in for the two header buttons. */
  appBarAddButton: {
    "&.MuiButton-root": {
      display: "flex",
      alignItems: "center",
      gap: tablet.appBarAction.gap,
      minWidth: 0,
      height: tablet.appBarAction.height,
      padding: `0 ${tablet.appBarAction.paddingX}`,
      background: secondaryButton.bg,
      border: `1px solid ${secondaryButton.border}`,
      borderRadius: tablet.appBarAction.radius,
      color: secondaryButton.color,
      fontSize: tablet.appBarAction.fontSize,
      fontWeight: secondaryButton.fontWeight,
      letterSpacing: "normal",
      lineHeight: "normal",
      textTransform: "none",
      whiteSpace: "nowrap",
      "&:hover": {
        background: tablet.appBarAction.hoverBg,
        border: `1px solid ${secondaryButton.border}`,
      },
    },
  } as SxProps<Theme>,

  /** Circled "+" — the phone's stand-in for the same two buttons. */
  appBarAddIconButton: {
    width: phone.appBarAction.size,
    height: phone.appBarAction.size,
    flexShrink: 0,
    borderRadius: phone.appBarAction.radius,
    color: secondaryButton.color,
    "&:hover": { bgcolor: phone.appBarAction.hoverBg },
  } as SxProps<Theme>,

  /**
   * Every button on this screen is declared under `&.MuiButton-root` rather
   * than flat. The global `MuiButton` overrides paint the fill with the
   * `background` **shorthand** and the size with the `padding` shorthand, and
   * those land after a flat `sx` rule of equal specificity — so a plain
   * `bgcolor` / `px` is silently reset and the design colour disappears. One
   * extra class in the selector settles it.
   *
   * `letterSpacing: normal` is not cosmetic either: MUI's button type step adds
   * `0.02857em`, which the design does not have.
   */
  secondaryButton: {
    "&.MuiButton-root": {
      display: "flex",
      alignItems: "center",
      gap: secondaryButton.gap,
      minWidth: 0,
      height: secondaryButton.height,
      padding: `0 ${secondaryButton.paddingX}`,
      background: secondaryButton.bg,
      border: `1px solid ${secondaryButton.border}`,
      borderRadius: secondaryButton.radius,
      color: secondaryButton.color,
      fontSize: secondaryButton.fontSize,
      fontWeight: secondaryButton.fontWeight,
      letterSpacing: "normal",
      lineHeight: "normal",
      textTransform: "none",
      whiteSpace: "nowrap",
      "&:hover": {
        background: secondaryButton.hoverBg,
        border: `1px solid ${secondaryButton.border}`,
      },
    },
  } as SxProps<Theme>,

  primaryButton: {
    "&.MuiButton-root": {
      display: "flex",
      alignItems: "center",
      gap: primaryButton.gap,
      minWidth: 0,
      height: primaryButton.height,
      padding: `0 ${primaryButton.paddingX}`,
      background: primaryButton.bg,
      border: "none",
      borderRadius: primaryButton.radius,
      color: primaryButton.color,
      fontSize: primaryButton.fontSize,
      fontWeight: primaryButton.fontWeight,
      letterSpacing: "normal",
      lineHeight: "normal",
      textTransform: "none",
      whiteSpace: "nowrap",
      boxShadow: "none",
      "&:hover": { background: primaryButton.hoverBg, boxShadow: "none" },
      "&.Mui-disabled": {
        background: button.disabledBg,
        color: button.disabledColor,
      },
    },
  } as SxProps<Theme>,

  /**
   * Master-detail card. Below `md` the reference has no card chrome at all —
   * the tablet paints its two panes directly on the viewport and the phone
   * shows one pane at a time straight on the page.
   */
  card: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    bgcolor: { xs: "transparent", md: card.bg },
    border: { xs: "none", md: `1px solid ${card.border}` },
    borderRadius: { xs: 0, md: card.radius },
    boxShadow: { xs: "none", md: card.shadow },
    overflow: "hidden",
  } as SxProps<Theme>,

  // ---------------------------------------------------------------- list pane

  listPane: {
    width: { xs: "100%", sm: tablet.listWidth, md: list.width },
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    bgcolor: { xs: "transparent", sm: tablet.listBg, md: "transparent" },
    borderRight: { sm: `1px solid ${list.divider}` },
  } as SxProps<Theme>,

  searchWrap: {
    p: {
      xs: `0 ${phone.paddingX.std} ${phone.gap.std}`,
      sm: tablet.searchPadding,
      md: list.searchPadding,
    },
    [PHONE_SM_QUERY]: {
      padding: `0 ${phone.paddingX.sm} ${phone.gap.sm}`,
    },
  } as SxProps<Theme>,

  searchField: {
    display: "flex",
    alignItems: "center",
    gap: { xs: phone.search.gap, sm: tablet.searchGap, md: list.searchGap },
    p: {
      xs: phone.search.padding,
      sm: tablet.searchFieldPadding,
      md: list.searchFieldPadding,
    },
    bgcolor: { xs: phone.search.bg, sm: list.searchBg },
    border: `1px solid ${list.searchBorder}`,
    borderRadius: {
      xs: phone.search.radius,
      sm: tablet.searchRadius,
      md: list.searchRadius,
    },
    boxSizing: "border-box",
    transition: "border-color .15s",
    "&:focus-within": { borderColor: list.searchFocusBorder },
  } as SxProps<Theme>,

  searchIcon: {
    width: {
      xs: phone.search.iconSize,
      sm: tablet.searchIconSize,
      md: list.searchIconSize,
    },
    height: {
      xs: phone.search.iconSize,
      sm: tablet.searchIconSize,
      md: list.searchIconSize,
    },
    flexShrink: 0,
    color: list.searchIconColor,
  } as SxProps<Theme>,

  searchInput: {
    flex: 1,
    minWidth: 0,
    fontSize: {
      xs: phone.search.fontSize.std,
      sm: tablet.searchFontSize,
      md: list.searchFontSize,
    },
    color: list.searchColor,
    // `normal` rather than MUI's 1.4375em: the design's field is a bare input,
    // so its height is the font's own line box. Anything else makes the search
    // box a few pixels taller than the design.
    lineHeight: "normal",
    "& .MuiInputBase-input": {
      p: list.searchInputPadding,
      height: "auto",
      lineHeight: "normal",
      fontSize: {
        xs: phone.search.fontSize.std,
        sm: tablet.searchFontSize,
        md: list.searchFontSize,
      },
      color: list.searchColor,
      "&::placeholder": { color: list.searchPlaceholder, opacity: 1 },
      [PHONE_SM_QUERY]: { fontSize: phone.search.fontSize.sm },
    },
    [PHONE_SM_QUERY]: { fontSize: phone.search.fontSize.sm },
  } as SxProps<Theme>,

  /**
   * "9 SHOWN" strip. The desktop reference dropped it, the phone never had it,
   * and only the tablet column still draws it — so it is rendered always and
   * shown at `sm` alone.
   */
  listMeta: {
    display: { xs: "none", sm: "flex", md: "none" },
    alignItems: "center",
    justifyContent: "space-between",
    p: { sm: tablet.metaPadding },
  } as SxProps<Theme>,

  listMetaLabel: {
    fontSize: list.metaFontSize,
    fontWeight: list.metaFontWeight,
    letterSpacing: tablet.metaLetterSpacing,
    color: list.metaColor,
    textTransform: "uppercase",
    lineHeight: "normal",
  } as SxProps<Theme>,

  listScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    p: {
      xs: `0 ${phone.paddingX.std} ${phone.paddingBottom.std}`,
      sm: tablet.scrollPadding,
      md: list.scrollPadding,
    },
    "&::-webkit-scrollbar": { width: list.scrollbarWidth },
    "&::-webkit-scrollbar-thumb": {
      background: list.scrollbarThumb,
      borderRadius: list.scrollbarRadius,
    },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    [PHONE_SM_QUERY]: {
      padding: `0 ${phone.paddingX.sm} ${phone.paddingBottom.sm}`,
    },
  } as SxProps<Theme>,

  /**
   * One contact row. The phone reference draws no selected state — a tap opens
   * the detail full-screen, so there is nothing left behind to mark.
   */
  listRow: (selected: boolean) =>
    ({
      display: "flex",
      alignItems: "center",
      gap: { xs: phone.row.gap, sm: tablet.rowGap, md: list.rowGap },
      width: "100%",
      p: {
        xs: phone.row.padding.std,
        sm: tablet.rowPadding,
        md: list.rowPadding,
      },
      mb: {
        xs: phone.row.marginBottom,
        sm: list.rowMarginBottom,
      },
      borderRadius: {
        xs: phone.row.radius,
        sm: tablet.rowRadius,
        md: list.rowRadius,
      },
      // No hairline at all on the phone: the reference's row is 68px, and a
      // transparent border would push it to 70.
      border: {
        xs: "none",
        sm: `1px solid ${selected ? list.rowSelectedBorder : "transparent"}`,
      },
      bgcolor: {
        xs: "transparent",
        sm: selected ? list.rowSelectedBg : "transparent",
      },
      textAlign: "left",
      textTransform: "none",
      justifyContent: "flex-start",
      "&:hover": {
        bgcolor: {
          xs: "transparent",
          sm: selected ? list.rowSelectedBg : list.rowHoverBg,
        },
      },
      "&.Mui-focusVisible": {
        outline: `2px solid ${list.searchFocusBorder}`,
        outlineOffset: "2px",
      },
      [PHONE_SM_QUERY]: { padding: phone.row.padding.sm },
    }) as SxProps<Theme>,

  listAvatar: (bg: string, color: string) =>
    ({
      width: {
        xs: phone.row.avatarSize.std,
        sm: tablet.avatarSize,
        md: list.avatarSize,
      },
      height: {
        xs: phone.row.avatarSize.std,
        sm: tablet.avatarSize,
        md: list.avatarSize,
      },
      flexShrink: 0,
      bgcolor: bg,
      color,
      fontSize: {
        xs: phone.row.avatarFontSize.std,
        sm: tablet.avatarFontSize,
        md: list.avatarFontSize,
      },
      fontWeight: list.avatarFontWeight,
      textTransform: "uppercase",
      [PHONE_SM_QUERY]: {
        width: phone.row.avatarSize.sm,
        height: phone.row.avatarSize.sm,
        fontSize: phone.row.avatarFontSize.sm,
      },
    }) as SxProps<Theme>,

  listRowText: {
    minWidth: 0,
    flex: 1,
  } as SxProps<Theme>,

  listRowName: {
    fontSize: {
      xs: phone.row.nameFontSize.std,
      sm: tablet.nameFontSize,
      md: list.nameFontSize,
    },
    fontWeight: list.nameFontWeight,
    color: list.nameColor,
    lineHeight: "normal",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    [PHONE_SM_QUERY]: { fontSize: phone.row.nameFontSize.sm },
  } as SxProps<Theme>,

  listRowEmail: {
    mt: {
      xs: list.emailMarginTop,
      sm: tablet.emailMarginTop,
      md: list.emailMarginTop,
    },
    fontSize: {
      xs: phone.row.emailFontSize.std,
      sm: tablet.emailFontSize,
      md: list.emailFontSize,
    },
    color: list.emailColor,
    lineHeight: "normal",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    [PHONE_SM_QUERY]: { fontSize: phone.row.emailFontSize.sm },
  } as SxProps<Theme>,

  listRowDot: {
    display: { xs: "none", sm: "block" },
    width: list.dotSize,
    height: list.dotSize,
    flexShrink: 0,
    borderRadius: "50%",
    bgcolor: list.dotColor,
  } as SxProps<Theme>,

  /** The phone's row affordance — a chevron rather than the selected dot. */
  listRowChevron: {
    display: { xs: "block", sm: "none" },
    width: phone.row.chevronSize,
    height: phone.row.chevronSize,
    flexShrink: 0,
    color: phone.row.chevronColor,
  } as SxProps<Theme>,

  listEmpty: {
    p: {
      xs: phone.empty.padding,
      sm: tablet.emptyPadding,
      md: list.emptyPadding,
    },
    textAlign: "center",
  } as SxProps<Theme>,

  listEmptyTitle: {
    fontSize: {
      xs: phone.empty.titleFontSize.std,
      sm: tablet.emptyTitleFontSize,
      md: list.emptyTitleFontSize,
    },
    fontWeight: list.emptyTitleFontWeight,
    color: list.emptyTitleColor,
    lineHeight: "normal",
    [PHONE_SM_QUERY]: { fontSize: phone.empty.titleFontSize.sm },
  } as SxProps<Theme>,

  listEmptySub: {
    // The tablet column shows the headline alone.
    display: { xs: "block", sm: "none", md: "block" },
    mt: list.emptySubMarginTop,
    fontSize: { xs: phone.empty.subFontSize.std, md: list.emptySubFontSize },
    color: list.emptySubColor,
    lineHeight: "normal",
    [PHONE_SM_QUERY]: { fontSize: phone.empty.subFontSize.sm },
  } as SxProps<Theme>,

  listLoader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    py: list.loaderPadding,
  } as SxProps<Theme>,

  // -------------------------------------------------------------- detail pane

  detailPane: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    overflowY: "auto",
    bgcolor: { xs: "transparent", sm: tablet.detailBg, md: "transparent" },
  } as SxProps<Theme>,

  detailInner: {
    p: {
      xs: phone.detail.padding.std,
      sm: tablet.detailPadding,
      md: detail.padding,
    },
    maxWidth: { xs: "none", md: detail.maxWidth },
    [PHONE_SM_QUERY]: { padding: phone.detail.padding.sm },
  } as SxProps<Theme>,

  detailHead: {
    display: "flex",
    alignItems: "center",
    gap: {
      xs: phone.detail.headGap.std,
      sm: tablet.headGap,
      md: detail.headGap,
    },
    mb: {
      xs: phone.detail.headMarginBottom.std,
      sm: tablet.headMarginBottom,
      md: detail.headMarginBottom,
    },
    [PHONE_SM_QUERY]: {
      gap: phone.detail.headGap.sm,
      marginBottom: phone.detail.headMarginBottom.sm,
    },
  } as SxProps<Theme>,

  detailBackButton: {
    width: detail.backButtonSize,
    height: detail.backButtonSize,
    flexShrink: 0,
    color: detail.backColor,
  } as SxProps<Theme>,

  detailAvatar: (bg: string, color: string) =>
    ({
      width: {
        xs: phone.detail.avatarSize.std,
        sm: tablet.detailAvatarSize,
        md: detail.avatarSize,
      },
      height: {
        xs: phone.detail.avatarSize.std,
        sm: tablet.detailAvatarSize,
        md: detail.avatarSize,
      },
      flexShrink: 0,
      bgcolor: bg,
      color,
      fontSize: {
        xs: phone.detail.avatarFontSize.std,
        sm: tablet.detailAvatarFontSize,
        md: detail.avatarFontSize,
      },
      fontWeight: detail.avatarFontWeight,
      textTransform: "uppercase",
      [PHONE_SM_QUERY]: {
        width: phone.detail.avatarSize.sm,
        height: phone.detail.avatarSize.sm,
        fontSize: phone.detail.avatarFontSize.sm,
      },
    }) as SxProps<Theme>,

  detailTitle: {
    m: 0,
    fontSize: {
      xs: phone.detail.titleFontSize.std,
      sm: tablet.titleFontSize,
      md: detail.titleFontSize,
    },
    fontWeight: detail.titleFontWeight,
    letterSpacing: detail.titleLetterSpacing,
    color: detail.titleColor,
    lineHeight: "normal",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    [PHONE_SM_QUERY]: { fontSize: phone.detail.titleFontSize.sm },
  } as SxProps<Theme>,

  detailSubtitle: {
    mt: {
      xs: phone.detail.subMarginTop,
      sm: tablet.subMarginTop,
      md: detail.subMarginTop,
    },
    fontSize: {
      xs: phone.detail.subFontSize.std,
      sm: tablet.subFontSize,
      md: detail.subFontSize,
    },
    color: detail.subColor,
    lineHeight: "normal",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    [PHONE_SM_QUERY]: { fontSize: phone.detail.subFontSize.sm },
  } as SxProps<Theme>,

  /**
   * White card the tablet reference wraps the form in. The phone and the
   * desktop draw the fields straight on their pane, so it is chrome-less there.
   */
  formCard: {
    bgcolor: { xs: "transparent", sm: tablet.formCard.bg, md: "transparent" },
    border: {
      xs: "none",
      sm: `1px solid ${tablet.formCard.border}`,
      md: "none",
    },
    borderRadius: { xs: 0, sm: tablet.formCard.radius, md: 0 },
    p: { xs: 0, sm: tablet.formCard.padding, md: 0 },
  } as SxProps<Theme>,

  sectionHeading: {
    display: "flex",
    alignItems: "center",
    gap: {
      xs: detail.sectionGap,
      sm: tablet.sectionGap,
      md: detail.sectionGap,
    },
    mt: {
      xs: phone.detail.sectionMarginTop.std,
      sm: tablet.sectionMarginTop,
      md: detail.sectionMarginTop,
    },
    mb: {
      xs: phone.detail.sectionMarginBottom.std,
      sm: tablet.sectionMarginBottom,
      md: detail.sectionMarginBottom,
    },
    [PHONE_SM_QUERY]: {
      marginTop: phone.detail.sectionMarginTop.sm,
      marginBottom: phone.detail.sectionMarginBottom.sm,
    },
  } as SxProps<Theme>,

  sectionHeadingLabel: {
    fontSize: {
      xs: phone.detail.sectionFontSize.std,
      sm: tablet.sectionFontSize,
      md: detail.sectionFontSize,
    },
    fontWeight: detail.sectionFontWeight,
    letterSpacing: detail.sectionLetterSpacing,
    color: detail.sectionColor,
    lineHeight: "normal",
    flexShrink: 0,
    [PHONE_SM_QUERY]: { fontSize: phone.detail.sectionFontSize.sm },
  } as SxProps<Theme>,

  sectionHeadingRule: {
    flex: 1,
    height: "1px",
    bgcolor: detail.sectionRule,
  } as SxProps<Theme>,

  // -------------------------------------------------------------- form fields

  /** Two-column field grid (names, phone/job, company/department). */
  fieldGrid: fieldGrid as SxProps<Theme>,

  /** Three-column grid used by City / State / Zip Code. */
  fieldGridThirds: fieldGridThirds as SxProps<Theme>,

  /** Spacing between consecutive field blocks. */
  fieldBlock: fieldBlock as SxProps<Theme>,

  /** Two-column grid that follows an earlier field block. */
  fieldGridBlock: {
    ...fieldGrid,
    ...fieldBlock,
    [PHONE_SM_QUERY]: mergePhoneQuery(fieldGrid, fieldBlock),
  } as SxProps<Theme>,

  /** Three-column grid that follows an earlier field block. */
  fieldGridThirdsBlock: {
    ...fieldGridThirds,
    ...fieldBlock,
    [PHONE_SM_QUERY]: mergePhoneQuery(fieldGridThirds, fieldBlock),
  } as SxProps<Theme>,

  /**
   * Contact Number and Department take a whole row on the phone: the reference
   * pairs Job Title with Company there instead of following the desktop's
   * (number | job) and (company | department) rows.
   */
  fieldSpanRow: { gridColumn: { xs: "1 / -1", sm: "auto" } } as SxProps<Theme>,

  /**
   * Country closes the address block. On the phone it is simply the second cell
   * of the Zip row; from `sm` up it drops onto its own row at half width, the
   * way both the tablet and desktop references draw it.
   */
  countryCell: {
    gridColumn: { xs: "auto", sm: "1 / -1" },
    maxWidth: {
      sm: `calc(50% - ${tablet.field.columnGap} / 2)`,
      md: `calc(50% - ${field.columnGap} / 2)`,
    },
  } as SxProps<Theme>,

  fieldLabel: {
    ...contactsStylesFieldLabel,
    ml: 0,
    color: field.labelColor,
    fontSize: {
      xs: phone.field.labelFontSize.std,
      sm: tablet.field.labelFontSize,
      md: field.labelFontSize,
    },
    fontWeight: field.labelFontWeight,
    lineHeight: "normal",
    // MUI prefixes the asterisk with a thin space; the design uses a full one,
    // so make up the difference rather than leaving "Name*" run together.
    "& .MuiFormLabel-asterisk": {
      color: field.requiredColor,
      ml: field.asteriskGap,
    },
    [PHONE_SM_QUERY]: { fontSize: phone.field.labelFontSize.sm },
  } as SxProps<Theme>,

  /** Gap between a field's label and its box, per column of the reference. */
  labelSpacing: {
    xs: field.labelGap,
    sm: tablet.field.labelGap,
    md: field.labelGap,
  },

  /** Text field carrying a trailing "n/max" counter (desktop only). */
  textField: {
    ...outlinedField,
    "& .MuiOutlinedInput-input": {
      ...outlinedField["& .MuiOutlinedInput-input"],
      p: counterInputPadding,
    },
  } as SxProps<Theme>,

  /** Text field with no counter (Street, City, State, Zip Code). */
  plainTextField: {
    ...outlinedField,
    "& .MuiOutlinedInput-input": {
      ...outlinedField["& .MuiOutlinedInput-input"],
      p: plainInputPadding,
    },
  } as SxProps<Theme>,

  counter: {
    fontSize: field.counterFontSize,
    fontWeight: field.counterFontWeight,
    color: field.counterColor,
    lineHeight: "normal",
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  /** Number half of the phone control — same chrome as `textField`. */
  phoneField: {
    ...outlinedField,
    "& .MuiOutlinedInput-input": {
      ...outlinedField["& .MuiOutlinedInput-input"],
      p: phoneNumberInputPadding,
    },
  } as SxProps<Theme>,

  /** Country dropdown — the same box with a trailing chevron. */
  countrySelect: {
    ...fieldBox,
    width: "100%",
    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
    "&:hover": { borderColor: field.hoverBorder },
    "&.Mui-focused": { borderColor: field.focusBorder },
    "&.Mui-disabled": {
      bgcolor: field.disabledBg,
      borderColor: field.disabledBorder,
    },
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      height: "100%",
      minHeight: "unset",
      boxSizing: "border-box",
      p: {
        xs: `0 ${field.paddingX} !important`,
        sm: `0 ${tablet.field.paddingX} !important`,
        md: `0 ${field.paddingX} !important`,
      },
      fontSize: {
        xs: phone.field.countryFontSize.std,
        sm: tablet.field.countryFontSize,
        md: field.fontSize,
      },
      fontWeight: field.selectFontWeight,
      color: field.color,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      "&.Mui-disabled": {
        WebkitTextFillColor: field.disabledColor,
        color: field.disabledColor,
      },
      [PHONE_SM_QUERY]: { fontSize: phone.field.countryFontSize.sm },
    },
  } as SxProps<Theme>,

  countryPlaceholder: {
    color: field.placeholder,
  } as SxProps<Theme>,

  /**
   * The design's chevron is the trailing item of a `space-between` row inside
   * the select, so it sits `paddingX` in from the *padding* box — one border
   * further than an `absolute` offset would put it. `top/bottom: 0` with auto
   * margins centres it the same way flex does, without transform rounding.
   */
  selectChevron: {
    position: "absolute",
    right: {
      xs: `calc(${field.paddingX} + ${field.borderWidth})`,
      sm: `calc(${tablet.field.paddingX} + ${field.borderWidth})`,
      md: `calc(${field.paddingX} + ${field.borderWidth})`,
    },
    top: 0,
    bottom: 0,
    m: "auto 0",
    width: {
      xs: field.selectChevronSize,
      sm: tablet.field.chevronSize,
      md: field.selectChevronSize,
    },
    height: {
      xs: field.selectChevronSize,
      sm: tablet.field.chevronSize,
      md: field.selectChevronSize,
    },
    color: field.chevronColor,
    pointerEvents: "none",
  } as SxProps<Theme>,

  // ------------------------------------------------------------ form actions

  /**
   * The phone reference stacks the actions full-width with no rule above them;
   * the tablet and desktop keep the single rule-topped row.
   */
  formActions: {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    alignItems: { xs: "stretch", sm: "center" },
    flexWrap: { xs: "nowrap", sm: "wrap" },
    gap: {
      xs: phone.detail.footerGap,
      sm: tablet.footerGap,
      md: detail.footerGap,
    },
    mt: {
      xs: phone.detail.footerMarginTop.std,
      sm: tablet.footerMarginTop,
      md: detail.footerMarginTop,
    },
    pt: { xs: 0, sm: tablet.footerPaddingTop, md: detail.footerPaddingTop },
    borderTop: { xs: "none", sm: `1px solid ${detail.footerBorder}` },
    [PHONE_SM_QUERY]: { marginTop: phone.detail.footerMarginTop.sm },
  } as SxProps<Theme>,

  saveButton: {
    "&.MuiButton-root": {
      width: { xs: "100%", sm: "auto" },
      minWidth: 0,
      height: {
        xs: phone.formActions.saveHeight.std,
        sm: tablet.formActions.height,
        md: formActions.saveHeight,
      },
      padding: {
        xs: 0,
        sm: `0 ${tablet.formActions.savePaddingX}`,
        md: `0 ${formActions.savePaddingX}`,
      },
      background: formActions.saveBg,
      borderRadius: {
        xs: formActions.radius,
        sm: tablet.formActions.radius,
        md: formActions.radius,
      },
      color: formActions.saveColor,
      fontSize: {
        xs: phone.formActions.saveFontSize.std,
        sm: tablet.formActions.saveFontSize,
        md: formActions.saveFontSize,
      },
      fontWeight: formActions.fontWeight,
      letterSpacing: "normal",
      lineHeight: "normal",
      textTransform: "none",
      boxShadow: "none",
      "&:hover": { background: formActions.saveHoverBg, boxShadow: "none" },
      "&.Mui-disabled": {
        background: button.disabledBg,
        color: button.disabledColor,
      },
      [PHONE_SM_QUERY]: {
        height: phone.formActions.saveHeight.sm,
        fontSize: phone.formActions.saveFontSize.sm,
      },
    },
  } as SxProps<Theme>,

  /** Cancel has no phone counterpart — the app-bar back arrow stands in. */
  cancelButton: {
    "&.MuiButton-root": {
      display: { xs: "none", sm: "inline-flex" },
      minWidth: 0,
      height: { sm: tablet.formActions.height, md: formActions.saveHeight },
      padding: {
        sm: `0 ${tablet.formActions.cancelPaddingX}`,
        md: `0 ${formActions.cancelPaddingX}`,
      },
      background: formActions.cancelBg,
      border: `1px solid ${formActions.cancelBorder}`,
      borderRadius: { sm: tablet.formActions.radius, md: formActions.radius },
      color: formActions.cancelColor,
      fontSize: {
        sm: tablet.formActions.cancelFontSize,
        md: formActions.cancelFontSize,
      },
      fontWeight: formActions.fontWeight,
      letterSpacing: "normal",
      lineHeight: "normal",
      textTransform: "none",
      "&:hover": {
        background: formActions.cancelBg,
        color: formActions.cancelHoverColor,
        border: `1px solid ${formActions.cancelHoverBorder}`,
      },
    },
  } as SxProps<Theme>,

  deleteButton: {
    "&.MuiButton-root": {
      ml: { xs: 0, sm: "auto" },
      width: { xs: "100%", sm: "auto" },
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: {
        xs: formActions.deleteGap,
        sm: tablet.formActions.deleteGap,
        md: formActions.deleteGap,
      },
      minWidth: 0,
      height: {
        xs: phone.formActions.saveHeight.std,
        sm: tablet.formActions.height,
        md: formActions.saveHeight,
      },
      padding: {
        xs: 0,
        sm: `0 ${tablet.formActions.deletePaddingX}`,
        md: `0 ${formActions.deletePaddingX}`,
      },
      background: formActions.deleteBg,
      border: `1px solid ${formActions.deleteBorder}`,
      borderRadius: {
        xs: formActions.radius,
        sm: tablet.formActions.radius,
        md: formActions.radius,
      },
      color: formActions.deleteColor,
      fontSize: {
        xs: phone.formActions.deleteFontSize.std,
        sm: tablet.formActions.deleteFontSize,
        md: formActions.deleteFontSize,
      },
      fontWeight: formActions.fontWeight,
      letterSpacing: "normal",
      lineHeight: "normal",
      textTransform: "none",
      whiteSpace: "nowrap",
      "&:hover": {
        background: formActions.deleteHoverBg,
        border: `1px solid ${formActions.deleteHoverBorder}`,
      },
      [PHONE_SM_QUERY]: {
        height: phone.formActions.saveHeight.sm,
        fontSize: phone.formActions.deleteFontSize.sm,
      },
    },
  } as SxProps<Theme>,

  /** Trash glyph inside the delete button. */
  deleteIcon: {
    width: {
      xs: phone.formActions.deleteIconSize.std,
      sm: tablet.formActions.deleteIconSize,
      md: formActions.deleteIconSize,
    },
    height: {
      xs: phone.formActions.deleteIconSize.std,
      sm: tablet.formActions.deleteIconSize,
      md: formActions.deleteIconSize,
    },
    [PHONE_SM_QUERY]: {
      width: phone.formActions.deleteIconSize.sm,
      height: phone.formActions.deleteIconSize.sm,
    },
  } as SxProps<Theme>,

  // ------------------------------------------------------------- fresh state

  /**
   * Layout only. The card chrome that used to live here moved onto the empty
   * state's own well (`framed`) — a bordered card wrapping a tinted panel drew
   * two boxes for one message. What is left is the page gutter the phone needs
   * and the vertical centring in whatever height the list would have filled.
   */
  freshWrap: {
    flex: "0 0 auto",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    mx: { xs: phone.paddingX.std, sm: 0 },
    [PHONE_SM_QUERY]: {
      marginLeft: phone.paddingX.sm,
      marginRight: phone.paddingX.sm,
    },
    // `SystemStyleObject`, not `SxProps` — the one call site composes it into
    // an array (`sx={[styles.freshWrap, {...}]}`), and `SxProps` itself admits
    // the array form, which TS rejects as an array element.
  } as SystemStyleObject<Theme>,


  freshIcon: {
    width: { xs: phone.freshState.iconSize.std, sm: freshState.iconSize },
    height: { xs: phone.freshState.iconSize.std, sm: freshState.iconSize },
    mb: {
      xs: phone.freshState.iconMarginBottom.std,
      sm: freshState.iconMarginBottom,
    },
    borderRadius: "50%",
    bgcolor: freshState.iconBg,
    color: freshState.iconStroke,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    [PHONE_SM_QUERY]: {
      width: phone.freshState.iconSize.sm,
      height: phone.freshState.iconSize.sm,
      marginBottom: phone.freshState.iconMarginBottom.sm,
    },
  } as SxProps<Theme>,

  freshIconGlyph: {
    width: {
      xs: phone.freshState.iconGlyphSize.std,
      sm: freshState.iconGlyphSize,
    },
    height: {
      xs: phone.freshState.iconGlyphSize.std,
      sm: freshState.iconGlyphSize,
    },
    [PHONE_SM_QUERY]: {
      width: phone.freshState.iconGlyphSize.sm,
      height: phone.freshState.iconGlyphSize.sm,
    },
  } as SxProps<Theme>,

  freshTitle: {
    fontSize: {
      xs: phone.freshState.titleFontSize.std,
      sm: freshState.titleFontSize,
    },
    fontWeight: freshState.titleFontWeight,
    color: freshState.titleColor,
    lineHeight: "normal",
    [PHONE_SM_QUERY]: { fontSize: phone.freshState.titleFontSize.sm },
  } as SxProps<Theme>,

  freshBody: {
    mt: freshState.bodyMarginTop,
    maxWidth: freshState.bodyMaxWidth,
    fontSize: {
      xs: phone.freshState.bodyFontSize.std,
      sm: freshState.bodyFontSize,
    },
    color: freshState.bodyColor,
    lineHeight: freshState.bodyLineHeight,
    [PHONE_SM_QUERY]: { fontSize: phone.freshState.bodyFontSize.sm },
  } as SxProps<Theme>,

  freshActions: {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    flexWrap: "wrap",
    alignItems: { xs: "stretch", sm: "center" },
    justifyContent: "center",
    width: { xs: "100%", sm: "auto" },
    gap: { xs: phone.freshState.actionsGap, sm: freshState.actionsGap },
    mt: {
      xs: phone.freshState.actionsMarginTop.std,
      sm: freshState.actionsMarginTop,
    },
    [PHONE_SM_QUERY]: { marginTop: phone.freshState.actionsMarginTop.sm },
  } as SxProps<Theme>,

  freshPrimaryButton: {
    "&.MuiButton-root": {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: primaryButton.gap,
      width: { xs: "100%", sm: "auto" },
      minWidth: 0,
      height: freshState.buttonHeight,
      padding: { xs: 0, sm: `0 ${freshState.primaryPaddingX}` },
      background: primaryButton.bg,
      borderRadius: {
        xs: phone.freshState.radiusButton,
        sm: freshState.radius,
      },
      color: primaryButton.color,
      fontSize: freshState.fontSize,
      fontWeight: freshState.fontWeight,
      letterSpacing: "normal",
      lineHeight: "normal",
      textTransform: "none",
      boxShadow: "none",
      "&:hover": { background: primaryButton.hoverBg, boxShadow: "none" },
    },
  } as SxProps<Theme>,

  freshSecondaryButton: {
    "&.MuiButton-root": {
      width: { xs: "100%", sm: "auto" },
      minWidth: 0,
      height: freshState.buttonHeight,
      padding: { xs: 0, sm: `0 ${freshState.secondaryPaddingX}` },
      background: freshState.secondaryBg,
      border: `1px solid ${freshState.secondaryBorder}`,
      borderRadius: {
        xs: phone.freshState.radiusButton,
        sm: freshState.radius,
      },
      color: freshState.secondaryColor,
      fontSize: freshState.fontSize,
      fontWeight: freshState.fontWeight,
      letterSpacing: "normal",
      lineHeight: "normal",
      textTransform: "none",
      "&:hover": {
        background: freshState.secondaryBg,
        border: `1px solid ${freshState.secondaryHoverBorder}`,
        color: freshState.secondaryHoverColor,
      },
    },
  } as SxProps<Theme>,

  /** Wrapper the fresh-state buttons need so a `Tooltip` can size with them. */
  freshActionSlot: {
    display: "inline-flex",
    width: { xs: "100%", sm: "auto" },
  } as SxProps<Theme>,

  // ---------------------------------------------------------------- skeletons

  skeleton: skeletonFill as SxProps<Theme>,

  /** Placeholder standing in for the "1,192 total contacts" line. */
  skeletonSubtitle: {
    ...skeletonFill,
    mt: header.subtitleMarginTop,
  } as SxProps<Theme>,

  /** Placeholder shaped like one form field, at the column's own height. */
  /**
   * A real field is a label line, `labelGap`, then the 44px input — 64px in
   * all. The placeholder used to stand in for the input alone, so the form was
   * 20px short per field and the pane jumped as the contact loaded.
   */
  skeletonFieldLabel: {
    ...contactsStylesFieldLabel,
    width: "40%",
  } as SxProps<Theme>,

  skeletonField: {
    ...skeletonFill,
    mt: field.labelGap,
    height: fieldHeight,
    borderRadius: fieldRadius,
    [PHONE_SM_QUERY]: fieldHeightNarrow,
  } as SxProps<Theme>,

  /** Avatar-sized placeholder in the list rows. */
  skeletonAvatar: {
    ...skeletonFill,
    width: {
      xs: phone.row.avatarSize.std,
      sm: tablet.avatarSize,
      md: list.avatarSize,
    },
    height: {
      xs: phone.row.avatarSize.std,
      sm: tablet.avatarSize,
      md: list.avatarSize,
    },
    flexShrink: 0,
    [PHONE_SM_QUERY]: {
      width: phone.row.avatarSize.sm,
      height: phone.row.avatarSize.sm,
    },
  } as SxProps<Theme>,

  /** Avatar-sized placeholder in the detail head. */
  skeletonDetailAvatar: {
    ...skeletonFill,
    width: {
      xs: phone.detail.avatarSize.std,
      sm: tablet.detailAvatarSize,
      md: detail.avatarSize,
    },
    height: {
      xs: phone.detail.avatarSize.std,
      sm: tablet.detailAvatarSize,
      md: detail.avatarSize,
    },
    flexShrink: 0,
    [PHONE_SM_QUERY]: {
      width: phone.detail.avatarSize.sm,
      height: phone.detail.avatarSize.sm,
    },
  } as SxProps<Theme>,

  /**
   * One row of the detail-pane placeholder: a two-column grid for the paired
   * rows, plain for the full-width ones, with the block spacing on all but the
   * first.
   */
  skeletonRow: (columns: number, isFirst: boolean) =>
    ({
      ...(columns === 2 ? fieldGrid : {}),
      ...(isFirst ? {} : fieldBlock),
      [PHONE_SM_QUERY]: mergePhoneQuery(
        columns === 2 ? fieldGrid : {},
        isFirst ? {} : fieldBlock,
      ),
    }) as SxProps<Theme>,
};

export default contactsStyles;
